import { FALLBACK_AVATAR_OPTIONS } from "../../../utils/profileAvatarCatalog";
import { LEAGUE_BULLION_BATTLE_STAKE, LEAGUE_VIEWER_USER_ID } from "./constants";
import { startingCoinsFor } from "./leagueFormat";
import type {
    League,
    LeagueFixture,
    LeagueFriend,
    LeagueGameKey,
    LeagueGameStats,
    LeagueMember,
    LeaguePlay,
    LeaguePlayMarketDetails,
    LeagueReactionActivity,
    LeagueSettlement,
} from "./types";

const HOUR = 3600000;
const DAY = 24 * HOUR;

function presetAvatar(index: number): string | null {
    const option = FALLBACK_AVATAR_OPTIONS[index % FALLBACK_AVATAR_OPTIONS.length];
    return option?.thumb_urls?.["96"] ?? option?.url ?? null;
}

/**
 * Hardcoded fixture shortlist. Real implementation reads `/trade/prioritised-matches`
 * or `/api/fixturelist`; the shape below is deliberately close to those rows.
 */
export function prototypeFixtures(now: number = Date.now()): LeagueFixture[] {
    return [
        {
            id: "proto-ind-wi",
            name: "India v West Indies",
            homeTeam: "India",
            awayTeam: "West Indies",
            competition: "ICC Men's T20 World Cup",
            start: new Date(now + 90 * 60000).toISOString(),
            durationHours: 5,
            formatOvers: 20,
            availableGames: ["sixes", "bullion", "trader", "squares"],
        },
        {
            id: "proto-aus-eng",
            name: "Australia v England",
            homeTeam: "Australia",
            awayTeam: "England",
            competition: "Ashes ODI Series",
            start: new Date(now + 1 * DAY + 2 * HOUR).toISOString(),
            durationHours: 9,
            formatOvers: 50,
            availableGames: ["bullion", "trader"],
        },
        {
            id: "proto-mi-csk",
            name: "Mumbai Indians v Chennai Super Kings",
            homeTeam: "Mumbai Indians",
            awayTeam: "Chennai Super Kings",
            competition: "Indian Premier League",
            start: new Date(now + 2 * DAY + 5 * HOUR).toISOString(),
            durationHours: 4,
            formatOvers: 20,
            availableGames: ["sixes", "bullion", "trader", "squares"],
        },
        {
            id: "proto-pak-nz",
            name: "Pakistan v New Zealand",
            homeTeam: "Pakistan",
            awayTeam: "New Zealand",
            competition: "T20I Series",
            start: new Date(now + 3 * DAY).toISOString(),
            durationHours: 4,
            formatOvers: 20,
            availableGames: ["sixes", "trader", "squares"],
        },
        {
            id: "proto-sa-sl",
            name: "South Africa v Sri Lanka",
            homeTeam: "South Africa",
            awayTeam: "Sri Lanka",
            competition: "ICC Men's T20 World Cup",
            start: new Date(now + 4 * DAY + 6 * HOUR).toISOString(),
            durationHours: 4,
            formatOvers: 20,
            availableGames: ["sixes", "bullion", "trader"],
        },
    ];
}

/** Stand-in for `GET /account/friends`. */
export const PROTOTYPE_FRIENDS: LeagueFriend[] = [
    { userId: 101, username: "rohan_84", playerName: "Rohan", avatar: presetAvatar(0), isOnline: true, xpWeekCrown: "gold" },
    { userId: 102, username: "meera.k", playerName: "Meera", avatar: presetAvatar(1), isOnline: true },
    { userId: 103, username: "dazza", playerName: "Darren", avatar: presetAvatar(2), isOnline: false },
    { userId: 104, username: "priya_p", playerName: "Priya", avatar: presetAvatar(3), isOnline: true, xpWeekCrown: "silver" },
    { userId: 105, username: "big_kev", playerName: "Kev", avatar: presetAvatar(4), isOnline: false },
    { userId: 106, username: "sanjay7", playerName: "Sanjay", avatar: presetAvatar(5), isOnline: false },
    { userId: 107, username: "aisha", playerName: "Aisha", avatar: presetAvatar(6), isOnline: true },
    { userId: 108, username: "tomtom", playerName: "Tom", avatar: presetAvatar(7), isOnline: false },
    { userId: 109, username: "nadia_c", playerName: "Nadia", avatar: presetAvatar(8), isOnline: true },
    { userId: 110, username: "vik", playerName: "Vikram", avatar: presetAvatar(9), isOnline: false },
    { userId: 111, username: "dev_r", playerName: "Dev", avatar: presetAvatar(10), isOnline: true },
    { userId: 112, username: "anika", playerName: "Anika", avatar: presetAvatar(11), isOnline: true, xpWeekCrown: "bronze" },
    { userId: 113, username: "jamal_k", playerName: "Jamal", avatar: presetAvatar(12), isOnline: true },
    { userId: 114, username: "sana.m", playerName: "Sana", avatar: presetAvatar(13), isOnline: true },
    { userId: 115, username: "chris_t", playerName: "Chris", avatar: presetAvatar(14), isOnline: false },
    { userId: 116, username: "riya8", playerName: "Riya", avatar: presetAvatar(15), isOnline: true },
    { userId: 117, username: "omar_b", playerName: "Omar", avatar: presetAvatar(16), isOnline: true },
];

const liveFriendsById = new Map<number, LeagueFriend>();

export function rememberLiveFriends(friends: LeagueFriend[]): void {
    friends.forEach((friend) => liveFriendsById.set(friend.userId, friend));
}

export function friendById(userId: number): LeagueFriend | null {
    return liveFriendsById.get(userId) ?? PROTOTYPE_FRIENDS.find((f) => f.userId === userId) ?? null;
}

const EMPTY_GAME_STATS: Record<LeagueGameKey, LeagueGameStats> = {
    trader: { pl: 0, wins: 0, losses: 0, played: 0 },
    bullion: { pl: 0, wins: 0, losses: 0, played: 0 },
    sixes: { pl: 0, wins: 0, losses: 0, played: 0 },
    squares: { pl: 0, wins: 0, losses: 0, played: 0 },
};

export function emptyGameStats(): Record<LeagueGameKey, LeagueGameStats> {
    return JSON.parse(JSON.stringify(EMPTY_GAME_STATS));
}

export function newMember(params: {
    userId: number;
    playerName: string;
    avatar?: string | null;
    xpWeekCrown?: string | null;
    isOnline?: boolean;
    isHost?: boolean;
    isViewer?: boolean;
    entryFeeRubies: number;
    /** Match format overs — drives seat chips (T10 → 1000, T20 → 2000, F50 → 3000). */
    formatOvers?: number | null;
    joinedAt?: string;
    reactions?: LeagueMember["reactions"];
}): LeagueMember {
    const startingCoins = startingCoinsFor(params.entryFeeRubies, params.formatOvers);
    return {
        userId: params.userId,
        playerName: params.playerName,
        avatar: params.avatar ?? null,
        xpWeekCrown: params.xpWeekCrown ?? null,
        isOnline: params.isOnline ?? true,
        isHost: params.isHost === true,
        isViewer: params.isViewer === true,
        joinedAt: params.joinedAt ?? new Date().toISOString(),
        startingCoins,
        coins: startingCoins,
        seatTopups: 0,
        reactions: params.reactions ?? {
            sent: 0,
            received: { poop: 0, rofl: 0, rocket: 0, fire: 0, clap: 0, cry: 0 },
        },
        byGame: emptyGameStats(),
    };
}

/** Seeds a member with plausible mid-league numbers so tables are not empty on first load. */
function seededMember(
    friendId: number | "viewer",
    entryFeeRubies: number,
    perGame: Partial<Record<LeagueGameKey, [pl: number, wins: number, losses: number]>>,
    options: {
        isHost?: boolean;
        joinedAt?: string;
        /** Shown as a movement arrow on first paint. */
        previousRank?: number;
        rankMovedAt?: string;
        reactions?: LeagueMember["reactions"];
    } = {},
): LeagueMember {
    const friend = friendId === "viewer" ? null : friendById(friendId);
    const member = newMember({
        userId: friendId === "viewer" ? LEAGUE_VIEWER_USER_ID : friendId,
        playerName: friendId === "viewer" ? "Alex" : friend?.playerName ?? "Player",
        avatar: friend?.avatar ?? null,
        xpWeekCrown: friend?.xpWeekCrown ?? null,
        isOnline: friendId === "viewer" ? true : Boolean(friend?.isOnline),
        isHost: options.isHost === true,
        isViewer: friendId === "viewer",
        entryFeeRubies,
        formatOvers: 20,
        joinedAt: options.joinedAt,
        reactions: options.reactions,
    });
    let pl = 0;
    (Object.keys(perGame) as LeagueGameKey[]).forEach((key) => {
        const entry = perGame[key];
        if (!entry) return;
        const [gamePl, wins, losses] = entry;
        member.byGame[key] = { pl: gamePl, wins, losses, played: wins + losses };
        pl += gamePl;
    });
    member.coins = member.startingCoins + pl;
    if (options.previousRank != null) member.previousRank = options.previousRank;
    if (options.rankMovedAt) member.rankMovedAt = options.rankMovedAt;
    return member;
}

/** Seeds an in-play bet so the "in play" list is populated on first load. */
function openPlay(
    member: LeagueMember,
    game: LeagueGameKey,
    stake: number,
    selection: string,
    multiplier: number,
    placedSecondsAgo: number,
    settlesInSeconds: number,
    marketDetails?: LeaguePlayMarketDetails,
): LeaguePlay {
    const now = Date.now();
    return {
        id: `seed-play-${member.userId}-${game}-${placedSecondsAgo}`,
        userId: member.userId,
        playerName: member.playerName,
        game,
        stake,
        selection,
        multiplier,
        placedAt: new Date(now - placedSecondsAgo * 1000).toISOString(),
        settlesAt: new Date(now + settlesInSeconds * 1000).toISOString(),
        status: "open",
        ...(marketDetails ? { marketDetails } : {}),
    };
}

function settlement(
    at: number,
    member: LeagueMember,
    game: LeagueGameKey,
    delta: number,
    stake: number,
    detail: string,
    marketDetails?: LeaguePlayMarketDetails,
): LeagueSettlement {
    return {
        id: `seed-${member.userId}-${game}-${at}`,
        at: new Date(at).toISOString(),
        userId: member.userId,
        playerName: member.playerName,
        game,
        outcome: delta >= 0 ? "win" : "loss",
        stake,
        delta,
        detail,
        ...(marketDetails ? { marketDetails } : {}),
    };
}

function seededThrow(
    members: LeagueMember[],
    now: number,
    fromUserId: number,
    targetUserId: number,
    emoji: NonNullable<LeagueReactionActivity["emoji"]>,
    agoMs: number,
): LeagueReactionActivity {
    const from = members.find((m) => m.userId === fromUserId);
    const target = members.find((m) => m.userId === targetUserId);
    return {
        id: `seed-throw-${fromUserId}-${targetUserId}-${agoMs}`,
        at: new Date(now - agoMs).toISOString(),
        fromUserId,
        fromPlayerName: from?.playerName?.trim() || `Player ${fromUserId}`,
        targetUserId,
        targetPlayerName: target?.playerName?.trim() || `Player ${targetUserId}`,
        emoji,
    };
}

export const WALKTHROUGH_LIVE_LEAGUE_ID = "proto-league-ind-wi";
export const PRESENTER_LEAGUE_CODE = "IND8WI";
/** Kickoff offset while the presenter is still in prematch. */
export const PRESENTER_PREMATCH_MS = 90 * 60000;

const PRESENTER_HOST_ID = 101;

function memberById(members: LeagueMember[], userId: number): LeagueMember {
    const found = members.find((row) => row.userId === userId);
    if (!found) throw new Error(`Missing presenter seed member ${userId}`);
    return found;
}

/**
 * Single invited India v West Indies room for the `/leaguesdemo` presenter walkthrough.
 * Viewer is not seated until they click Join. Host is Rohan.
 */
export function buildWalkthroughLiveLeague(now: number = Date.now()): League {
    const fixtures = prototypeFixtures(now);
    const indVsWi = fixtures[0]!;
    const fee = 5;
    const startMs = now + PRESENTER_PREMATCH_MS;
    const joinAgo = (hours: number) => new Date(now - hours * HOUR).toISOString();

    const emptyReceived = () =>
        ({ poop: 0, rofl: 0, rocket: 0, fire: 0, clap: 0, cry: 0 }) as NonNullable<
            LeagueMember["reactions"]
        >["received"];
    const members: LeagueMember[] = [
        seededMember(
            PRESENTER_HOST_ID,
            fee,
            { trader: [80, 1, 0] },
            {
                isHost: true,
                joinedAt: joinAgo(30),
                reactions: {
                    sent: 1,
                    received: { ...emptyReceived(), rocket: 2, clap: 1 },
                },
            },
        ),
        seededMember(102, fee, { sixes: [0, 0, 0] }, {
            joinedAt: joinAgo(28),
            reactions: { sent: 2, received: { ...emptyReceived(), poop: 1 } },
        }),
        seededMember(104, fee, { bullion: [0, 0, 0] }, {
            joinedAt: joinAgo(26),
            reactions: { sent: 0, received: { ...emptyReceived(), rofl: 3, cry: 1 } },
        }),
        seededMember(107, fee, { trader: [40, 1, 0] }, {
            joinedAt: joinAgo(22),
            reactions: { sent: 3, received: emptyReceived() },
        }),
        seededMember(109, fee, { sixes: [0, 0, 0] }, {
            joinedAt: joinAgo(20),
            reactions: { sent: 0, received: { ...emptyReceived(), poop: 2, fire: 1 } },
        }),
        seededMember(103, fee, {}, { joinedAt: joinAgo(18) }),
        seededMember(105, fee, { trader: [0, 0, 0] }, { joinedAt: joinAgo(16) }),
        seededMember(111, fee, { sixes: [0, 0, 0] }, {
            joinedAt: joinAgo(14),
            reactions: { sent: 1, received: { ...emptyReceived(), rocket: 4 } },
        }),
        seededMember(112, fee, { bullion: [0, 0, 0] }, { joinedAt: joinAgo(12) }),
        seededMember(113, fee, { trader: [20, 1, 0] }, {
            joinedAt: joinAgo(10),
            reactions: { sent: 2, received: emptyReceived() },
        }),
        seededMember(114, fee, {}, { joinedAt: joinAgo(8) }),
        seededMember(116, fee, { sixes: [0, 0, 0] }, {
            joinedAt: joinAgo(6),
            reactions: { sent: 1, received: emptyReceived() },
        }),
        seededMember(117, fee, { bullion: [0, 0, 0] }, { joinedAt: joinAgo(4) }),
        seededMember(110, fee, {}, { joinedAt: joinAgo(2) }),
    ];

    const rohan = memberById(members, 101);
    const meera = memberById(members, 102);
    const priya = memberById(members, 104);
    const aisha = memberById(members, 107);
    const nadia = memberById(members, 109);
    const jamal = memberById(members, 113);
    const anika = memberById(members, 112);
    const dev = memberById(members, 111);

    return {
        id: WALKTHROUGH_LIVE_LEAGUE_ID,
        code: PRESENTER_LEAGUE_CODE,
        name: "India v WI Ruby Rumble",
        scope: "match",
        entryFeeRubies: fee,
        createdAt: new Date(now - 2 * DAY).toISOString(),
        startsAt: new Date(startMs).toISOString(),
        endsAt: new Date(startMs + indVsWi.durationHours * HOUR).toISOString(),
        fixture: { ...indVsWi, start: new Date(startMs).toISOString() },
        hostUserId: PRESENTER_HOST_ID,
        hostName: "Rohan",
        members,
        invitedUserIds: [LEAGUE_VIEWER_USER_ID, 115],
        membership: "invited",
        presenterMatchStarted: false,
        settlements: [],
        plays: [
            openPlay(rohan, "trader", 200, "India @ 1.72", 1.72, 900, 3600, {
                marketName: "Match Winner",
                selectionName: "India",
                marketClass: "Win",
            }),
            openPlay(aisha, "trader", 150, "West Indies @ 2.15", 2.15, 720, 3600, {
                marketName: "Match Winner",
                selectionName: "West Indies",
                marketClass: "Win",
            }),
            openPlay(jamal, "trader", 80, "Over 165.5 @ 1.88", 1.88, 480, 3600, {
                marketName: "India Innings Runs",
                selectionName: "Over",
                marketClass: "Line",
                marketLine: "165.5",
            }),
            openPlay(meera, "sixes", 0, "One card", 1, 640, 7200, { cardScore: 0 }),
            openPlay(nadia, "sixes", 0, "One card", 1, 400, 7200, { cardScore: 0 }),
            openPlay(dev, "sixes", 0, "One card", 1, 220, 7200, { cardScore: 0 }),
            openPlay(priya, "bullion", 500, "India v WI Ruby Rumble Battle", 3, 800, 7200),
            openPlay(anika, "bullion", 500, "India v WI Ruby Rumble Battle", 3, 360, 7200),
        ],
        bullionBattle: {
            name: "India v WI Ruby Rumble Battle",
            entryStake: LEAGUE_BULLION_BATTLE_STAKE,
            status: "open",
            viewerEntered: false,
            enteredUserIds: [104, 112],
            ensuredAt: new Date(now - 6 * HOUR).toISOString(),
        },
        includedGames: ["sixes", "bullion", "trader", "squares"],
        reactionEvents: [
            seededThrow(members, now, 102, 104, "rofl", 8 * 60_000),
            seededThrow(members, now, 107, 101, "rocket", 14 * 60_000),
            seededThrow(members, now, 113, 109, "poop", 22 * 60_000),
            seededThrow(members, now, 116, 111, "rocket", 31 * 60_000),
        ],
    };
}

export function prototypeLeagues(now: number = Date.now()): League[] {
    return [buildWalkthroughLiveLeague(now)];
}
